// Step 6 — time slot grid + cross-location expansion (ADR 0003).
//
// Primary: per-fitter slots at the selected location, OR fanned-out
// "Any Fitter" slots at the selected location.
//
// Cross-location: "View other locations for additional availability"
// fires parallel /availability calls to nearby locations' resources via
// the api-client (which fans out per-location to its fitters). Promise.allSettled
// inside the client means one bad location doesn't kill the batch.

import { useContext, useEffect, useState } from 'react';
import styles from '../styles/widget.module.css';
import { getAvailability, listResources, listLocations, type MergedSlot } from '../api/client';
import { resolveWireName } from '../data/fittingNames';
import { formatSlotTime } from '../utils/datetime';
import { haversineMiles } from '../utils/distance';
import { FitterAssignmentContext } from '../utils/fitterAssignment';
import { isReadOnly } from '../utils/readOnly';
import { waitlistOffered } from '../config/countries';
import type { Location, Resource } from '../types';
import type { BookingAction, BookingState } from '../state/machine';
import { AlertMessage } from '../components/AlertMessage';

interface TimeSlotsProps {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  /** Called when the user clicks "Add me to the waitlist" in the no-availability
   *  empty state. Handler lives in widget.tsx (calls /waitlist + transitions
   *  to the congrats screen with a WL-prefixed confirmation number). Only
   *  rendered where the market offers a waitlist — see waitlistOffered(). */
  onJoinWaitlist: () => void;
  disabled?: boolean;
}

interface SlotGroup {
  location: Location;
  slots: MergedSlot[];
  loading?: boolean;
  error?: string;
}

// Cap how many nearby locations we fan out to. Wireframe shows 2 in the
// "View other locations" expansion; we'll allow a few more so the user has
// real options. Each adds N parallel /availability calls (one per fitter at
// that location), so keep it bounded.
const MAX_NEARBY_LOCATIONS = 5;
const NEARBY_LOCATION_RADIUS_MILES = 100;

export function TimeSlots({ state, dispatch, onJoinWaitlist, disabled = false }: TimeSlotsProps) {
  const [primary, setPrimary] = useState<SlotGroup | null>(null);
  const [showOthers, setShowOthers] = useState(false);
  const [others, setOthers] = useState<SlotGroup[]>([]);
  const [othersLoading, setOthersLoading] = useState(false);

  // Reset the alt-locations panel whenever the primary location changes —
  // picking an alt slot promotes that location to primary, and the
  // previously-fanned-out 'others' list becomes stale (and would include
  // the new primary, causing visual slot-mirroring across grids).
  // Cache the resource and location lists so cross-location fanout doesn't refetch
  const [allResources, setAllResources] = useState<Resource[] | null>(null);
  const [allLocations, setAllLocations] = useState<Location[] | null>(null);

  const { selectedLocation, selectedDate, selectedFittingType, selectedFitterId } = state;
  // Who takes an Any Fitter time: the mount's rule, set by
  // data-cc-fitter-assignment (utils/fitterAssignment.ts).
  const fitterAssignment = useContext(FitterAssignmentContext);

  // A pending slot promised upstream (the seed card's NEXT AVAILABLE row)
  // auto-selects once the real list confirms it still exists. One shot either
  // way: found → selected and cleared; gone → cleared and the picker opens
  // normally, because asserting a taken time is worse than asking again.
  useEffect(() => {
    if (!state.pendingSlotStart || !primary || primary.loading) return;
    const promised = primary.slots.find((s) => s.start === state.pendingSlotStart);
    if (promised && !state.selectedSlot) {
      dispatch({ type: 'SELECT_SLOT', payload: promised });
    }
    dispatch({ type: 'SET_PENDING_SLOT', payload: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pendingSlotStart, primary]);

  // Any-Fitter attribution drift. The location merge assigns each time's
  // fitter uniformly at random PER FETCH — deliberate, it spreads bookings
  // across the roster — so a grid reload (the fitting-name map arriving is
  // enough) can hand the selected time to a different fitter, and a
  // selection keyed on start+fitterId silently loses its highlight: visibly
  // "selected, then unselected". Under Any Fitter the TIME is the choice
  // and the fitter is incidental, so the selection rebinds to the reloaded
  // grid's candidate for the same time. A pinned fitter is left alone —
  // silently rebooking a different person is worse than asking again.
  useEffect(() => {
    if (!primary || primary.loading || !state.selectedSlot || state.selectedFitterId) return;
    const sel = state.selectedSlot;
    if (primary.slots.some((s) => s.start === sel.start && s.fitterId === sel.fitterId)) return;
    const sameTime = primary.slots.find((s) => s.start === sel.start);
    if (sameTime) {
      dispatch({ type: 'SELECT_SLOT', payload: sameTime });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary]);

  // Country-scoped locations: force refetch when state.country changes so a
  // switch from US to UK doesn't reuse the previous country's cached list.
  useEffect(() => {
    setAllLocations(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.country]);

  // Fetch resources + locations. Locations refetch when country changes (via
  // the setAllLocations(null) above). Null country → middleware default.
  useEffect(() => {
    if (!allResources) listResources().then(setAllResources).catch(() => setAllResources([]));
    if (!allLocations) listLocations(state.country).then(setAllLocations).catch(() => setAllLocations([]));
  }, [allResources, allLocations, state.country]);

  // Clear the alt-locations panel on every primary-location change
  useEffect(() => {
    setShowOthers(false);
    setOthers([]);
    setOthersLoading(false);
  }, [selectedLocation?.id]);

  // Fetch primary location availability
  useEffect(() => {
    if (!selectedLocation || !selectedDate || !selectedFittingType || !allResources) return;

    setPrimary({ location: selectedLocation, slots: [], loading: true });

    const fittersAtLoc = allResources.filter((r) => r.locationId === selectedLocation.id);
    const chosenFitter = selectedFitterId
      ? fittersAtLoc.find((r) => r.id === selectedFitterId)
      : null;

    const wireName = resolveWireName(selectedFittingType.name, state.fittingNameMap);
    const request = chosenFitter
      ? {
        resource: chosenFitter,
        date: selectedDate,
        durationMin: selectedFittingType.durationMinutes,
        fittingType: wireName,
      }
      : {
        location: selectedLocation,
        knownResources: fittersAtLoc,
        date: selectedDate,
        durationMin: selectedFittingType.durationMinutes,
        fittingType: wireName,
        fitterAssignment,
      };

    getAvailability(request)
      .then((res) => setPrimary({ location: selectedLocation, slots: res.slots }))
      .catch((err) =>
        setPrimary({
          location: selectedLocation,
          slots: [],
          error: err instanceof Error ? err.message : 'Failed to load availability',
        }),
      );
  }, [selectedLocation, selectedDate, selectedFittingType, selectedFitterId, allResources, state.fittingNameMap, fitterAssignment]);

  // Compute "nearby locations" from the SELECTED location's coords (not the
  // user's original zip), capped at MAX_NEARBY_LOCATIONS within radius.
  // Locations without lat/lng on the middleware response are excluded —
  // they can't be ranked.
  const findNearby = (): Location[] => {
    if (!selectedLocation || !allLocations) return [];
    if (selectedLocation.lat === undefined || selectedLocation.lng === undefined) return [];
    const selectedCoords = { lat: selectedLocation.lat, lng: selectedLocation.lng };

    const candidates: Array<{ loc: Location; distance: number }> = [];
    for (const loc of allLocations) {
      if (loc.id === selectedLocation.id) continue;
      if (loc.lat === undefined || loc.lng === undefined) continue;
      const distance = haversineMiles(selectedCoords, { lat: loc.lat, lng: loc.lng });
      if (distance > NEARBY_LOCATION_RADIUS_MILES) continue;
      candidates.push({ loc, distance });
    }
    candidates.sort((a, b) => a.distance - b.distance);
    return candidates.slice(0, MAX_NEARBY_LOCATIONS).map((c) => c.loc);
  };

  // Fan out to nearby locations on demand (ADR 0003). Results are appended
  // as each promise settles so the user sees locations populate progressively
  // instead of staring at a list of empty placeholders.
  const expandOthers = async () => {
    setShowOthers(true);
    setOthers([]);

    if (!selectedDate || !selectedFittingType || !allResources) return;

    const nearby = findNearby();
    if (nearby.length === 0) return;

    setOthersLoading(true);

    // Nearby fanout stays within radius (currently 100mi) — same-country in
    // practice — so we reuse the primary store's wireName map. If we ever
    // widen the radius to span countries, this needs per-nearby-location
    // /fittings resolution instead.
    const wireName = resolveWireName(selectedFittingType.name, state.fittingNameMap);
    await Promise.all(
      nearby.map((loc) =>
        getAvailability({
          location: loc,
          knownResources: allResources.filter((r) => r.locationId === loc.id),
          date: selectedDate,
          durationMin: selectedFittingType.durationMinutes,
          fittingType: wireName,
          fitterAssignment,
        })
          .then((res) => {
            if (res.slots.length > 0) {
              setOthers((prev) => [...prev, { location: loc, slots: res.slots }]);
            }
          })
          .catch(() => {
            /* swallow per-location failure; user just doesn't see that location */
          }),
      ),
    );

    setOthersLoading(false);
  };

  // Hide the "View other locations" link entirely when there's nothing to show
  const hasNearbyCandidates = findNearby().length > 0;

  return (
    <div className={disabled ? styles.panelSectionDisabled : undefined}>
      {primary?.loading && (
        <p className={styles.subtitle}>
          <span className={styles.spinner} />
          Loading times…
        </p>
      )}
      {primary?.error && <AlertMessage description={primary?.error} />}

      {primary && !primary.loading && primary.slots.length === 0 && !primary.error && (
        <>
          <AlertMessage
            type='warning'
            title='No times available'
            description={`No times available on ${selectedDate} at ${selectedLocation?.name}. Try a different date
            or fitter.`}
          />
          {/* AU does not work a waitlist, so its customers see the message alone. */}
          {!isReadOnly() && waitlistOffered(state.country) && (
            <WaitlistCta state={state} onJoinWaitlist={onJoinWaitlist} />
          )}
        </>
      )}

      {primary && primary.slots.length > 0 && (
        <>
          <p className={styles.slotsAvailabilityInfo}>
            <strong>{primary.slots.length}</strong> available time{primary.slots.length === 1 ? '' : 's'} at{' '}
            <strong>{primary.location.name}</strong>
          </p>
          <SlotGrid
            slots={primary.slots}
            selectedSlot={state.selectedSlot}
            onPick={(slot) => dispatch({ type: 'SELECT_SLOT', payload: slot })}
          />
        </>
      )}

      {primary && !showOthers && hasNearbyCandidates && (
        <button className={styles.linkButton} onClick={expandOthers} style={{ marginTop: 16 }}>
          View other locations for additional availability
        </button>
      )}

      {showOthers && (
        <div style={{ marginTop: 16 }}>
          <button className={styles.linkButton} onClick={() => setShowOthers(false)}>
            Hide other locations
          </button>

          {othersLoading && others.length === 0 && (
            <p className={styles.subtitle}>
              <span className={styles.spinner} />
              Checking nearby locations…
            </p>
          )}

          {!othersLoading && others.length === 0 && (
            <p className={styles.subtitle}>
              No additional availability at nearby locations on this date.
            </p>
          )}

          {others.map((group) => (
            <div key={group.location.id} style={{ marginTop: 12 }}>
              <p className={styles.subtitle}>
                <strong>{group.location.name.toUpperCase()}</strong> (Any Fitter)
              </p>
              <SlotGrid
                slots={group.slots}
                selectedSlot={state.selectedSlot}
                onPick={(slot) => {
                  // Picking a slot at another location switches the user there
                  // atomically — going through the individual per-field actions
                  // tripped the cascading resets in the reducer (e.g.
                  // SELECT_FITTING_TYPE clears date).
                  dispatch({
                    type: 'SWITCH_TO_SLOT_AT_LOCATION',
                    payload: {
                      location: group.location,
                      fittingType: selectedFittingType!,
                      date: selectedDate!,
                      slot,
                    },
                  });
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface SlotGridProps {
  slots: MergedSlot[];
  selectedSlot: { start: string; fitterId: string } | null;
  onPick: (slot: MergedSlot) => void;
}

// Waitlist call-to-action — shown beneath any no-availability error message
// so the user has an actionable next step. POSTs to /waitlist (pending Druv);
// on success the widget routes to the congrats screen with a WL- number.
function WaitlistCta({
  state,
  onJoinWaitlist,
}: {
  state: BookingState;
  onJoinWaitlist: () => void;
}) {
  return (
    <div className={styles.waitlistCtaWrapper}>
      <button
        className={`${styles.button} ${styles.outline}`}
        disabled={state.waitlistSubmitting}
        data-cc-track="cta"
        data-cc-track-id="join_waitlist"
        onClick={onJoinWaitlist}
      >
        {state.waitlistSubmitting ? (
          <>
            <span className={styles.spinner} />
            Joining waitlist…
          </>
        ) : (
          'Add me to the waitlist'
        )}
      </button>
      {state.waitlistError && (
        <AlertMessage title={`Couldn't join the waitlist`} description={state.waitlistError} />
      )}
    </div>
  );
}

function SlotGrid({ slots, selectedSlot, onPick }: SlotGridProps) {
  return (
    <div className={styles.timeSlotGrid}>
      {slots.map((slot) => {
        const label = formatSlotTime(slot.start);
        // Match both start AND fitterId so a 2:00 PM slot at one location
        // doesn't visually highlight the 2:00 PM slot at another location
        // when they happen to share the same ISO timestamp.
        const isSelected =
          selectedSlot?.start === slot.start && selectedSlot?.fitterId === slot.fitterId;
        return (
          <button
            key={slot.start + slot.fitterId}
            className={`${styles.timeSlot} ${isSelected ? styles.selected : ''}`}
            onClick={() => onPick(slot)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
